/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  rem,
  createTheme,
  Button as MantineButton,
  type MantineTheme,
  type ButtonStylesNames,
  type ButtonCssVariables,
} from '@mantine/core';

import { colorToken, typographyToken, variablesToken } from '@fai/design-tokens';

import * as classes from './button.css';

// TODO: add test cases for switch cases.
export const buttonTheme = createTheme({
  components: {
    Button: MantineButton.extend({
      defaultProps: { theme: 'neutral', variant: 'primary', size: 'md' },
      vars: (_, { theme, variant, size, disabled, loading }) => {
        let root: Partial<Record<ButtonCssVariables['root'], string>> = {
          '--button-radius': variablesToken.radius.sm,
        };

        let color = undefined;
        let hoverColor = undefined;
        let borderColor = undefined;

        switch (theme) {
          case 'neutral': {
            if (variant === 'primary') {
              color = colorToken.color.bg.neutral.strong.base;
              hoverColor = colorToken.color.bg.neutral.strong.hover;
            } else {
              color = colorToken.color.fg.neutral.strong;
              hoverColor = colorToken.color.fg.neutral.default;
            }

            borderColor = colorToken.color.border.neutral.default;

            break;
          }
          case 'cloud': {
            if (variant === 'primary') {
              color = colorToken.color.bg.cloudblue.strong.base;
              hoverColor = colorToken.color.bg.cloudblue.strong.hover;
            } else {
              color = colorToken.color.fg.cloudblue.default.base;
              hoverColor = colorToken.color.fg.cloudblue.default.hover;
            }

            borderColor = colorToken.color.border.cloudblue.default;

            break;
          }
          case 'api': {
            if (variant === 'primary') {
              color = colorToken.color.bg.apiviolet.strong.base;
              hoverColor = colorToken.color.bg.apiviolet.strong.hover;
            } else {
              color = colorToken.color.fg.apiviolet.default.base;
              hoverColor = colorToken.color.fg.apiviolet.default.hover;
            }

            borderColor = colorToken.color.border.apiviolet.default;

            break;
          }
          case 'container': {
            if (variant === 'primary') {
              color = colorToken.color.bg.containergreen.strong.base;
              hoverColor = colorToken.color.bg.containergreen.strong.hover;
            } else {
              color = colorToken.color.fg.apigreen.base;
              hoverColor = colorToken.color.fg.apigreen.hover;
            }

            borderColor = colorToken.color.border.apigreen.default;

            break;
          }
          case 'danger': {
            if (variant === 'primary') {
              color = colorToken.color.bg.danger.strong.base;
              hoverColor = colorToken.color.bg.danger.strong.hover;
            } else {
              color = colorToken.color.fg.danger.default.base;
              hoverColor = colorToken.color.fg.danger.default.hover;
            }

            borderColor = colorToken.color.border.danger.default;

            break;
          }
        }

        switch (size) {
          case 'xs': {
            root = {
              ...root,
              '--button-fz': typographyToken.title['typography-title-2xs'].fontSize,
              '--button-height': rem(24),
              '--button-padding-x': variablesToken.layout['3xs'],
            };

            if (variant === 'icon' || variant === 'icon-transparent') {
              root = { ...root, '--button-padding-x': '0' };
            }

            break;
          }
          case 'sm': {
            root = {
              ...root,
              '--button-fz': typographyToken.title['typography-title-xs'].fontSize,
              '--button-height': rem(32),
              '--button-padding-x': variablesToken.layout['2xs'],
            };

            if (variant === 'icon' || variant === 'icon-transparent') {
              root = { ...root, '--button-padding-x': '0' };
            }

            break;
          }
          case 'md': {
            root = {
              ...root,
              '--button-fz': typographyToken.title['typography-title-sm'].fontSize,
              '--button-height': rem(40),
              '--button-padding-x': variablesToken.layout.xs,
            };

            if (variant === 'icon' || variant === 'icon-transparent') {
              root = { ...root, '--button-padding-x': '0' };
            }

            break;
          }
        }

        switch (variant) {
          case 'primary': {
            root = {
              ...root,
              '--button-bg': color,
              '--button-hover': hoverColor,
              '--button-color': colorToken.color.fg.white,
              '--button-hover-color': colorToken.color.fg.white,
              '--button-bd': 'none',
            };

            if (disabled || loading) {
              root = { ...root, '--button-color': colorToken.color.fg.white };
            }

            break;
          }
          case 'outline':
          case 'icon': {
            root = {
              ...root,
              '--button-bg': colorToken.color.fg.white,
              '--button-hover': colorToken.color.bg.neutral.default.hover,
              '--button-color': color,
              '--button-hover-color': color,
              '--button-bd': `${rem(1)} solid ${borderColor}`,
            };

            if (disabled || loading) {
              root = {
                ...root,
                '--button-color': colorToken.color.fg.neutral.disable,
              };
            }

            break;
          }
          case 'transparent':
          case 'icon-transparent': {
            root = {
              ...root,
              '--button-bg': 'transparent',
              '--button-hover': 'transparent',
              '--button-color': color,
              '--button-hover-color': hoverColor,
              '--button-bd': 'none',
            };

            if (disabled || loading) {
              root = {
                ...root,
                '--button-color': colorToken.color.fg.neutral.disable,
              };
            }

            break;
          }
          case 'pill': {
            root = {
              ...root,
              '--button-bg': colorToken.color.fg.white,
              '--button-hover': colorToken.color.bg.neutral.default.hover,
              '--button-color': color,
              '--button-hover-color': color,
              '--button-bd': `${rem(1)} solid ${borderColor}`,
              '--button-radius': variablesToken.radius.pill,
            };

            if (disabled || loading) {
              root = {
                ...root,
                '--button-color': colorToken.color.fg.neutral.disable,
              };
            }

            break;
          }
        }

        return { root };
      },
      classNames(_, { variant, size, disabled, loading }) {
        let classNames: Partial<Record<ButtonStylesNames, string>> = {};

        switch (size) {
          case 'xs': {
            classNames = {
              root: classes.rootXs,
              section: classes.sectionWithSmallGap,
            };

            if (variant === 'icon' || variant === 'icon-transparent') {
              classNames = { ...classNames, root: classes.rootXsIcon };
            }

            break;
          }
          case 'sm': {
            classNames = {
              root: classes.rootSm,
              section: classes.sectionWithSmallGap,
            };

            if (variant === 'icon' || variant === 'icon-transparent') {
              classNames = { ...classNames, root: classes.rootSmIcon };
            }

            break;
          }
          case 'md': {
            classNames = {
              root: classes.rootMd,
              section: classes.sectionWithLargeGap,
            };

            if (variant === 'icon' || variant === 'icon-transparent') {
              classNames = { ...classNames, root: classes.rootMdIcon };
            }

            break;
          }
        }

        if (disabled || loading) {
          switch (variant) {
            case 'primary': {
              classNames = {
                ...classNames,
                root: `${classNames.root} ${classes.rootPrimaryDisabled}`,
              };

              break;
            }
            case 'outline':
            case 'icon': {
              classNames = {
                ...classNames,
                root: `${classNames.root} ${classes.rootOutlineDisabled}`,
              };

              break;
            }
            case 'transparent':
            case 'icon-transparent': {
              classNames = {
                ...classNames,
                root: `${classNames.root} ${classes.rootTransparentDisabled}`,
              };

              break;
            }
            case 'pill': {
              classNames = {
                ...classNames,
                root: `${classNames.root} ${classes.rootPillDisabled}`,
              };

              break;
            }
          }
        }

        if (loading) {
          classNames = {
            ...classNames,
            inner: classes.innerLoading,
            loader: classes.loader,
          };
        } else {
          classNames = {
            ...classNames,
            inner: classes.inner,
            loader: classes.loaderLoading,
          };
        }

        return classNames;
      },
    }),
  },
  activeClassName: classes.active,
  focusClassName: classes.focus,
}) as MantineTheme;
